import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContextType, AuthState, ClinicLoginResult, LoginCredentials } from '@app-types/auth';
import {
  useMainGetUser,
  useMainLogin,
  useMainLogout,
  useMainRefreshToken,
  useUnifiedTenantGetUser,
  useUnifiedTenantLogout,
  useUnifiedTenantRefreshToken,
  type UnifiedTenantPrincipalType,
} from '@/hooks/main/useMainAuth';
import { authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';
import { getRouter } from '@/shared/lib/routerRef';
import { logoutSsoContext } from '@/features/sso/ssoLogout';
import {
  captureActiveSessionAuthGeneration,
  emitActiveSessionAuthGeneration,
  isActiveSessionAuthGenerationCurrent,
  runForCurrentActiveSessionAuthGeneration,
  settleActiveSessionSyncCooldown,
} from '@/features/sso/activeSessionSync';
import {
  canRecoverAnonymousP1ClinicMutation,
  runP1SsoAuthMutation,
} from '@/features/sso/ssoAuthMutationLock';
import {
  clearPortalSession,
  clearSsoSession,
  hasSsoSession,
  isSsoEnabled,
  isSsoSite,
  markPortalAuthChannel,
  normalizeUnifiedPortalStorage,
  readActivePortalAuthChannel,
  readPortalAuthTransport,
  selectExactPortalBearerToken,
  type PortalAuthTransport,
  type SsoEntryType,
  validateSsoLogoutUrl,
} from '@/features/sso/ssoSession';

const deriveSiteFromLocation = (): string => {
  const match = window.location.pathname.match(/^\/([^/]+)(?:\/|$)/);
  return match?.[1] || 'medix';
};
const SUPPRESS_FORCE_LOGOUT_TOAST_ONCE_KEY = 'suppress_force_logout_toast_once';
const HIDE_TENANT_LOGOUT_SESSION_KEY = 'ra2n_hide_tenant_logout';

export interface StoredAuthSnapshot {
  token: string | null;
  user: AuthState['user'];
  clinic: AuthState['clinic'];
  entryType: SsoEntryType;
  principalType: UnifiedTenantPrincipalType;
  transport: PortalAuthTransport | null;
}

const safeJsonRecord = (value: string | null): Record<string, unknown> | null => {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

const principalTypeOf = (user: Record<string, unknown> | null): UnifiedTenantPrincipalType => {
  const normalized = String(user?.principal_type ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (normalized === 'admin' || normalized.endsWith('admin')) return 'admin';
  if (['massager', 'practitioner', 'therapist'].includes(normalized)) return 'massager';
  return 'clinic_user';
};

export const readStoredAuthSnapshot = (
  mode: AuthProviderMode,
  site: string,
): StoredAuthSnapshot => {
  if (mode === 'unified' && isSsoSite(site)) {
    normalizeUnifiedPortalStorage(site, localStorage, sessionStorage);
  }
  const clinicToken = localStorage.getItem('auth_token');
  const clinicUser = safeJsonRecord(localStorage.getItem('auth_user'));
  const clinic = safeJsonRecord(localStorage.getItem('auth_clinic'));

  if (mode !== 'unified') {
    const clinicContext = isSsoSite(site)
      ? { site, entryType: 'clinic' } as const
      : undefined;
    const transport = clinicContext
      ? readPortalAuthTransport(clinicContext, localStorage) ??
        (hasSsoSession(clinicContext) ? 'cookie' : clinicToken ? 'bearer' : null)
      : clinicToken
        ? 'bearer'
        : null;
    return {
      token: transport === 'cookie' ? null : clinicToken,
      user: clinicUser as AuthState['user'],
      clinic: clinic as AuthState['clinic'],
      entryType: 'clinic',
      principalType: principalTypeOf(clinicUser),
      transport,
    };
  }

  // A unified tenant has exactly one browser auth channel. Principal type is
  // payload metadata used by RBAC, never a selector for another token/cookie.
  const channel = isSsoSite(site)
    ? readActivePortalAuthChannel(site, localStorage)
    : null;
  const selectedEntry = 'unified' as const;
  const selectedContext = { site, entryType: selectedEntry } as const;
  const unifiedChannel = channel?.entryType === selectedEntry ? channel : null;
  const selectedToken = unifiedChannel === null
    ? clinicToken
    : selectExactPortalBearerToken(selectedContext, localStorage);
  const legacyCookieSession = isSsoSite(site) && hasSsoSession(selectedContext);
  const transport =
    unifiedChannel?.transport ??
    (legacyCookieSession ? 'cookie' : selectedToken ? 'bearer' : null);

  return {
    token: transport === 'bearer' ? selectedToken : null,
    user: clinicUser as AuthState['user'],
    clinic: clinic as AuthState['clinic'],
    entryType: selectedEntry,
    principalType: principalTypeOf(clinicUser),
    transport,
  };
};

const isP1SsoAuthMutationEnabled = (): boolean =>
  [
    import.meta.env.VITE_SSO_ENABLED,
    import.meta.env.VITE_SSO_PASSIVE_ENABLED,
    import.meta.env.VITE_SSO_ACTIVE_SESSION_SYNC_ENABLED,
  ].some(isSsoEnabled);

function readHideTenantLogoutFromSession(): boolean {
  return sessionStorage.getItem(HIDE_TENANT_LOGOUT_SESSION_KEY) === '1';
}

function setHideTenantLogoutSession(value: boolean): void {
  if (value) {
    sessionStorage.setItem(HIDE_TENANT_LOGOUT_SESSION_KEY, '1');
  } else {
    sessionStorage.removeItem(HIDE_TENANT_LOGOUT_SESSION_KEY);
  }
}

/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the app.
 */

const initialState: AuthState = {
  user: null,
  clinic: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  hideTenantLogoutButton: false,
  authEntryType: 'clinic',
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export type AuthProviderMode = 'clinic' | 'passive' | 'unified';

interface AuthProviderProps {
  children: React.ReactNode;
  mode?: AuthProviderMode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, mode = 'clinic' }) => {
  const [state, setState] = useState<AuthState>(initialState);

  const loginMutation = useMainLogin();
  const logoutMutation = useMainLogout();
  const refreshTokenMutation = useMainRefreshToken();
  const unifiedLogoutMutation = useUnifiedTenantLogout();
  const unifiedRefreshMutation = useUnifiedTenantRefreshToken();
  const queryClient = useQueryClient();

  const derivedSite = deriveSiteFromLocation();
  const isPassiveMode = mode === 'passive';
  const isUnifiedMode = mode === 'unified';
  const [storedAuth, setStoredAuth] = useState<StoredAuthSnapshot>(() =>
    readStoredAuthSnapshot(mode, derivedSite)
  );
  const authContext = isSsoSite(derivedSite)
    ? { site: derivedSite, entryType: storedAuth.entryType } as const
    : undefined;
  const hasCookieSession = storedAuth.transport === 'cookie' || Boolean(
    authContext && hasSsoSession(authContext)
  );
  const authGeneration = authContext
    ? captureActiveSessionAuthGeneration(authContext, localStorage).generation
    : 0;
  const mainGetUserQuery = useMainGetUser(
    isPassiveMode || isUnifiedMode ? undefined : derivedSite
  );
  const unifiedGetUserQuery = useUnifiedTenantGetUser({
    site: derivedSite,
    principalType: storedAuth.principalType,
    principalId: storedAuth.user?.id,
    authGeneration,
    bearerToken: storedAuth.transport === 'bearer' ? storedAuth.token : null,
    enabled:
      isUnifiedMode &&
      !isPassiveMode &&
      !!storedAuth.user &&
      (storedAuth.transport === 'cookie' ||
        (storedAuth.transport === 'bearer' && !!storedAuth.token)),
  });
  const getUserQuery = isUnifiedMode ? unifiedGetUserQuery : mainGetUserQuery;

  // Initialize auth state from storage / server
  useEffect(() => {
    if (isPassiveMode) {
      setState({
        user: storedAuth.user,
        clinic: storedAuth.clinic,
        token: storedAuth.token,
        isAuthenticated: !!storedAuth.token && !!storedAuth.user,
        isLoading: false,
        error: null,
        hideTenantLogoutButton: readHideTenantLogoutFromSession(),
        authEntryType: storedAuth.entryType,
      });
      return;
    }

    if ((!storedAuth.token && !hasCookieSession) || !storedAuth.user) {
      setHideTenantLogoutSession(false);
      setState({
        ...initialState,
        isLoading: false,
        hideTenantLogoutButton: false,
        authEntryType: storedAuth.entryType,
      });
      return;
    }

    if (getUserQuery.isSuccess) {
      if (authContext && storedAuth.transport) {
        markPortalAuthChannel(authContext, storedAuth.transport, localStorage);
      }
      setState({
        user: getUserQuery.data.user,
        clinic: getUserQuery.data.clinic,
        token: storedAuth.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        hideTenantLogoutButton: readHideTenantLogoutFromSession(),
        authEntryType: storedAuth.entryType,
      });
      return;
    }

    if (getUserQuery.isError) {
      if (isSsoSite(derivedSite)) {
        clearPortalSession(
          { site: derivedSite, entryType: storedAuth.entryType },
          localStorage,
          sessionStorage,
        );
      } else if (storedAuth.entryType === 'admin') {
        localStorage.removeItem('site_admin_token');
        localStorage.removeItem('site_admin');
        localStorage.removeItem('site_admin_site');
      } else {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_clinic');
      }
      setHideTenantLogoutSession(false);
      setState({
        ...initialState,
        isLoading: false,
        hideTenantLogoutButton: false,
      });
      return;
    }

    // While verifying
    setState(prev => ({
      ...prev,
      isLoading: true,
    }));
  }, [
    getUserQuery.isSuccess,
    getUserQuery.isError,
    getUserQuery.data,
    isPassiveMode,
    isUnifiedMode,
    storedAuth.token,
    storedAuth.user,
    storedAuth.clinic,
    storedAuth.entryType,
    storedAuth.principalType,
    storedAuth.transport,
    hasCookieSession,
    authGeneration,
    derivedSite,
  ]);

  const login = async (credentials: LoginCredentials): Promise<ClinicLoginResult> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    const loginSite = credentials.site_type || derivedSite;
    const loginEntryType = isUnifiedMode ? 'unified' : 'clinic';
    const context = isSsoSite(loginSite)
      ? { site: loginSite, entryType: loginEntryType } as const
      : undefined;
    const ssoEnabled = context !== undefined && isP1SsoAuthMutationEnabled();
    const performLogin = async (): Promise<ClinicLoginResult> => {
      try {
        const response = await loginMutation.mutateAsync({
          ...credentials,
          authEntryType: loginEntryType,
        });

        if (response.success) {
          if (context) {
            clearSsoSession(context);
          }
          const hideLogout = !!response.data.from_admin;
          setHideTenantLogoutSession(hideLogout);
          sessionStorage.removeItem(SUPPRESS_FORCE_LOGOUT_TOAST_ONCE_KEY);
          setStoredAuth({
            token: response.data.token,
            user: response.data.user,
            clinic: response.data.clinic,
            entryType: loginEntryType,
            principalType: principalTypeOf(
              response.data.user as unknown as Record<string, unknown>
            ),
            transport: 'bearer',
          });
          setState({
            user: response.data.user,
            clinic: response.data.clinic,
            token: response.data.token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            hideTenantLogoutButton: hideLogout,
            authEntryType: loginEntryType,
          });
          if (ssoEnabled && context) {
            emitActiveSessionAuthGeneration(context, localStorage, window);
            // This sign-in just settled which session the browser holds, so the page it lands
            // on has nothing left to pick up from another Portal. Without the cooldown that
            // page opened one more broker round trip and the screen visibly flashed twice
            // after logging in, ending on a URL that briefly read /sso/... on the way past.
            settleActiveSessionSyncCooldown(context, sessionStorage, localStorage);
          }
          return {
            success: true,
            ssoEstablishUrl: response.data.sso_establish_url,
          };
        }

        const failMessage = response.message || 'ログインに失敗しました';
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: failMessage,
        }));
        return { success: false, message: failMessage };
      } catch (error: unknown) {
        const axiosError = error as {
          response?: {
            data?: { message?: string; errors?: { password?: string[] } };
          };
        };
        const data = axiosError?.response?.data;
        const message =
          data?.errors?.password?.[0] ||
          data?.message ||
          (error instanceof Error ? error.message : null) ||
          'ログインに失敗しました';
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: message,
        }));
        return { success: false, message };
      }
    };

    if (!context) {
      return performLogin();
    }

    try {
      const loginGeneration = captureActiveSessionAuthGeneration(context, localStorage);
      const result = await runP1SsoAuthMutation({
        context,
        ssoEnabled,
        storage: localStorage,
        operation: performLogin,
        // A page-load active-sync probe may commit a no-op while this click is
        // queued for the same Web Lock. The login is still safe only when the
        // exact click-time anonymous/auth generation remains current.
        canRunAfterCommittedMutation: () =>
          isActiveSessionAuthGenerationCurrent(loginGeneration, localStorage),
        canRecoverAbandonedMutation: () =>
          canRecoverAnonymousP1ClinicMutation({
            context,
            authGeneration: loginGeneration,
            localStorage,
            sessionStorage,
          }),
      });
      if (result.kind === 'completed') {
        return result.value;
      }
    } catch {
      // The response could not be committed safely. Keep the current session unchanged.
    }

    const message = 'ログインを安全に完了できませんでした。もう一度お試しください。';
    setState(prev => ({ ...prev, isLoading: false, error: message }));
    return { success: false, message };
  };

  const logout = async (): Promise<void> => {
    const resolvedSite = state.clinic?.parent_site_type || state.clinic?.site_type || derivedSite;
    const logoutSite = resolvedSite.toLowerCase().includes('upsystem') ? 'medix' : resolvedSite;
    const entryType = isUnifiedMode ? storedAuth.entryType : 'clinic';
    const loginPath = entryType === 'admin'
      ? `/${logoutSite}/admin/login`
      : `/${logoutSite}/login`;
    const portalContext = isSsoSite(logoutSite)
      ? { site: logoutSite, entryType } as const
      : undefined;
    const activeChannel = portalContext
      ? readActivePortalAuthChannel(portalContext.site, localStorage)
      : null;
    const ssoContext = portalContext &&
      (hasSsoSession(portalContext) ||
        activeChannel?.entryType === entryType && activeChannel.transport === 'cookie')
        ? portalContext
        : undefined;
    const generationContext = isSsoSite(logoutSite)
      ? { site: logoutSite, entryType } as const
      : undefined;
    const logoutGeneration = generationContext
      ? captureActiveSessionAuthGeneration(generationContext, localStorage)
      : undefined;
    const ssoEnabled = generationContext !== undefined && isP1SsoAuthMutationEnabled();
    let redirectUrl = loginPath;

    const performLogout = async (): Promise<void> => {
      try {
        if (ssoContext) {
          redirectUrl = await logoutSsoContext(ssoContext);
        } else {
          const response = isUnifiedMode
            ? await unifiedLogoutMutation.mutateAsync({
                site: derivedSite,
                entryType,
                bearerToken: storedAuth.token,
              })
            : await logoutMutation.mutateAsync({ site: derivedSite });
          redirectUrl = validateSsoLogoutUrl(
            response?.data?.logout_url,
            isSsoSite(logoutSite)
              ? { site: logoutSite, entryType }
              : undefined,
          ) ?? redirectUrl;
        }
      } catch (error) {
        console.error('Logout failed:', error);
      } finally {
        const finalizeLogout = (): void => {
          sessionStorage.setItem(SUPPRESS_FORCE_LOGOUT_TOAST_ONCE_KEY, '1');
          setHideTenantLogoutSession(false);
          if (generationContext) {
            clearPortalSession(generationContext, localStorage, sessionStorage);
          } else if (entryType === 'admin') {
            localStorage.removeItem('site_admin_token');
            localStorage.removeItem('site_admin');
            localStorage.removeItem('site_admin_site');
          } else {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            localStorage.removeItem('auth_clinic');
          }
          setStoredAuth({
            token: null,
            user: null,
            clinic: null,
            entryType: storedAuth.entryType,
            principalType: 'clinic_user',
            transport: null,
          });
          setState({ ...initialState, hideTenantLogoutButton: false });
          queryClient.clear();
          authorizeNavigation();
          if (ssoEnabled && generationContext) {
            emitActiveSessionAuthGeneration(generationContext, localStorage, window);
          }
          if (redirectUrl !== loginPath) {
            window.location.assign(redirectUrl);
          } else {
            getRouter().navigate(loginPath, { replace: true });
          }
        };

        if (logoutGeneration) {
          if (!runForCurrentActiveSessionAuthGeneration(
            logoutGeneration,
            localStorage,
            finalizeLogout,
          )) {
            return;
          }
        } else {
          finalizeLogout();
        }
      }
    };

    if (!generationContext) {
      await performLogout();
      return;
    }

    try {
      const result = await runP1SsoAuthMutation({
        context: generationContext,
        ssoEnabled,
        storage: localStorage,
        operation: performLogout,
      });
      if (result.kind === 'skipped' && result.reason === 'storage_unavailable') {
        // The sentinel could not be claimed (unreadable storage, or a claim the lock could not
        // vouch for). Signing out is the safe direction, and a button that silently does
        // nothing is what the operator reported: run the logout without the lock rather than
        // leave the session in place. finalizeLogout still re-checks the auth generation.
        console.warn('P1 SSO logout ran without the mutation lock.');
        await performLogout();
      }
    } catch {
      // Fail closed: do not start a second, uncoordinated logout mutation.
    }
  };

  const refreshToken = async (): Promise<void> => {
    const entryType = isUnifiedMode ? storedAuth.entryType : 'clinic';
    const context = isSsoSite(derivedSite)
      ? { site: derivedSite, entryType } as const
      : undefined;
    const ssoEnabled = context !== undefined && isP1SsoAuthMutationEnabled();
    const performRefresh = async (): Promise<boolean> => {
      try {
        const response = isUnifiedMode
          ? await unifiedRefreshMutation.mutateAsync({
              site: derivedSite,
              entryType,
              bearerToken: storedAuth.token,
            })
          : await refreshTokenMutation.mutateAsync({ site: derivedSite });
        if (response.success && (response.data?.token || hasCookieSession)) {
          if (response.data?.token) {
            const token = response.data.token;
            const credentialKey = entryType === 'admin'
              ? 'site_admin_token'
              : 'auth_token';
            localStorage.setItem(credentialKey, token);
            if (context) {
              markPortalAuthChannel(context, 'bearer', localStorage);
            }
            setStoredAuth(previous => ({ ...previous, token, transport: 'bearer' }));
            setState(prev => ({
              ...prev,
              token,
            }));
          }
          if (ssoEnabled && context) {
            emitActiveSessionAuthGeneration(context, localStorage, window);
          }
          return true;
        }
      } catch {
        // Run logout only after the mutation lock has been released.
      }
      return false;
    };

    if (!context) {
      if (!await performRefresh()) {
        await logout();
      }
      return;
    }

    let shouldLogout = false;
    try {
      const result = await runP1SsoAuthMutation({
        context,
        ssoEnabled,
        storage: localStorage,
        operation: performRefresh,
      });
      shouldLogout = result.kind === 'completed' && !result.value;
    } catch {
      // Fail closed when the response could not be committed safely.
      return;
    }

    if (shouldLogout) {
      await logout();
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 * Returns a default safe auth context if provider is not available (e.g., during HMR)
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  // Return context from localStorage if provider is not available (e.g., during HMR reloads)
  // This prevents ProtectedRoute from being stuck on "施設情報を読み込み中..." after HMR
  if (context === undefined) {
    const storedClinic = localStorage.getItem('auth_clinic');
    const storedUser = localStorage.getItem('auth_user');
    const storedToken = localStorage.getItem('auth_token');
    const clinic = storedClinic ? JSON.parse(storedClinic) : null;
    const user = storedUser ? JSON.parse(storedUser) : null;

    return {
      user,
      clinic: clinic ?? {
        id: 0,
        clinic_number: '',
        name: 'Default Clinic',
        site_type: 'medix',
        service_type: 'a2',
        is_enable_checkin: false,
      },
      token: storedToken,
      isAuthenticated: !!storedToken && !!user,
      isLoading: false,
      error: null,
      hideTenantLogoutButton: false,
      authEntryType: 'clinic',
      login: async () => ({ success: true }),
      logout: async () => {},
      refreshToken: async () => {},
    };
  }

  return context;
};

export default AuthContext;
